import { ChangeDetectionStrategy, Component } from '@angular/core';
import { injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDialogDescription, HlmDialogTitle } from '@spartan-ng/helm/dialog';

export const DISCLAIMER_ACK_KEY = 'horde-disclaimer-accepted';

export interface DisclaimerDialogContext {
    close: () => void;
}

/**
 * Startup disclaimer informing players the app is in Alpha: saved game data and spawn brackets may be cleared
 * between version updates, and Horde v4 is an explicit clean break from older saves. Shown until dismissed via
 * "Okay" (session only) or "Okay, don't tell me again" (persisted to localStorage).
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [HlmButtonImports, HlmDialogTitle, HlmDialogDescription],
    selector: 'app-disclaimer-dialog',
    template: `
        <div class="min-w-0 pr-8">
            <h2 class="text-lg font-semibold" hlmDialogTitle>Welcome to The Animus</h2>
            <p class="mt-1 text-sm text-muted-foreground" hlmDialogDescription>Alpha release notice</p>
        </div>

        <div class="mt-4 max-h-[min(65vh,36rem)] overflow-y-auto text-sm leading-6 text-foreground">
            <p>
                The Animus is currently in <span class="font-semibold">Alpha</span>. Saved game data and spawn
                brackets may be cleared at any time between version updates as the application is iterated on.
            </p>
            <p class="mt-3">
                Horde v4 is a clean break from earlier prelaunch versions. Existing saved games are not migrated or
                preserved, and future Alpha updates may reset them again.
            </p>
        </div>

        <div class="mt-4 flex flex-col justify-end gap-2 sm:flex-row">
            <button (click)="acknowledge()" hlmBtn variant="default" type="button">Okay</button>
            <button (click)="acknowledgePermanently()" hlmBtn variant="outline" type="button">
                Okay, don't tell me again
            </button>
        </div>
    `,
})
export class DisclaimerDialog {
    private readonly context = injectBrnDialogContext<DisclaimerDialogContext>();

    protected acknowledge(): void {
        this.context.close();
    }

    protected acknowledgePermanently(): void {
        try {
            localStorage.setItem(DISCLAIMER_ACK_KEY, 'accepted');
        } catch {
            // LocalStorage unavailable (private mode / SSR) - the dialog may show again next session.
        }

        this.context.close();
    }
}
